import {spawn} from 'child_process';
import {PassThrough, Readable} from 'stream';

import {getBugsnag} from '../utils/bugsnag.js';
import {MetricsExporter, MetricName} from '../utils/metrics-exporter.js';

import {DeploymentConfig, DeploymentHooks} from './types.js';

interface BuildProjectOptions {
  config: DeploymentConfig;
  assetPath?: string;
  hooks?: DeploymentHooks;
  metricsExporter?: MetricsExporter;
}

export async function buildProject(options: BuildProjectOptions) {
  const {config, assetPath, hooks} = options;
  const Bugsnag = getBugsnag();

  if (hooks?.buildFunction) {
    const startTime = performance.now();
    return hooks
      .buildFunction(assetPath)
      .then(() => {
        options.metricsExporter?.add(
          MetricName.BuildTime,
          performance.now() - startTime,
        );
      })
      .catch((error) => {
        throw new Error(`Build function failed with error: ${error}`);
      });
  }

  const assetPathEnvironment = assetPath
    ? {HYDROGEN_ASSET_BASE_URL: assetPath}
    : {};
  try {
    await new Promise((resolve, reject) => {
      let stderr = '';
      const stderrStream = new PassThrough();

      stderrStream.on('data', (chunk) => {
        stderr += chunk;
        process.stderr.write(chunk);
      });

      const startTime = performance.now();
      const buildCommand = spawn(config.buildCommand!, [], {
        stdio: ['inherit', 'pipe', 'pipe'],
        env: {
          // eslint-disable-next-line no-process-env
          ...process.env,
          ...assetPathEnvironment,
        },
        cwd: config.rootPath,
        shell: true,
      });

      (buildCommand.stderr as Readable | null)?.pipe(stderrStream);
      (buildCommand.stdout as Readable | null)?.pipe(stderrStream);

      buildCommand.on('close', (code) => {
        if (code === 0) {
          options.metricsExporter?.add(
            MetricName.BuildTime,
            performance.now() - startTime,
          );
        }
        Bugsnag?.addMetadata('buildCommand', {
          command: config.buildCommand,
          stderr,
          code,
        });
        if (code !== 0) {
          reject(code);
          return;
        }
        resolve(code);
      });
    });
  } catch (error) {
    throw new Error(`Build failed with error code: ${error}`);
  }
}
